import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import AttendanceTerminalPanel from "../components/AttendanceTerminalPanel";
import AttendanceGroupedByArea from "../components/AttendanceGroupedByArea";
import { useAuth } from "../../../app/providers/AuthProvider";
import { useEmpresa } from "../../../app/providers/EmpresaProvider";
import {
  getAttendanceDailySummary,
  getCurrentBranchId,
  getCurrentSessionToken,
  getCurrentLicenseId,
  getTodayYmd,
} from "../api/terminalAttendanceApi";
import Ilustracion from "../../../assets/Ilustracion.svg";

const AUTO_HIDE_AFTER_MINUTES = 60;
const REFRESH_INTERVAL_ACTIVE_MS = 30000;
const REFRESH_INTERVAL_EMPTY_MS = 60000;

function formatMinutes(minutes) {
  const total = Math.max(0, Number(minutes) || 0);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

function formatHour(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return "-";

  return d.toLocaleTimeString("es-AR", {
    timeZone: "America/Argentina/Buenos_Aires",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function shouldKeepRow(row, now = new Date()) {
  if (!row) return false;

  if (row.latestStatus === "OPEN") {
    return true;
  }

  if (!row.lastClockOutAt) {
    return true;
  }

  const outDate = new Date(row.lastClockOutAt);
  if (!Number.isFinite(outDate.getTime())) {
    return true;
  }

  const diffMs = now.getTime() - outDate.getTime();
  return diffMs < AUTO_HIDE_AFTER_MINUTES * 60 * 1000;
}

function sortRowsStable(rows) {
  return [...rows].sort((a, b) => {
    const aOpen = a?.latestStatus === "OPEN" ? 0 : 1;
    const bOpen = b?.latestStatus === "OPEN" ? 0 : 1;
    if (aOpen !== bOpen) return aOpen - bOpen;

    const aIn = a?.firstClockInAt
      ? new Date(a.firstClockInAt).getTime()
      : Number.MAX_SAFE_INTEGER;
    const bIn = b?.firstClockInAt
      ? new Date(b.firstClockInAt).getTime()
      : Number.MAX_SAFE_INTEGER;
    if (aIn !== bIn) return aIn - bIn;

    return String(a?.fullName || "").localeCompare(String(b?.fullName || ""));
  });
}

export default function AttendanceTerminalPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const {
    setEmpresaId,
    setEmpresaNombre,
    setSucursalId,
    sucursalNombre,
    setSucursalNombre,
  } = useEmpresa();

  const ymd = useMemo(() => getTodayYmd(), []);
  const [dailyRows, setDailyRows] = useState([]);

  const refreshIntervalMs = dailyRows.length
    ? REFRESH_INTERVAL_ACTIVE_MS
    : REFRESH_INTERVAL_EMPTY_MS;

  const refreshDailySummary = useCallback(async () => {
    const token = getCurrentSessionToken();
    const licenseId = getCurrentLicenseId();
    const branchId = getCurrentBranchId();

    if (!token || !licenseId || !branchId) {
      setDailyRows([]);
      return;
    }

    try {
      const response = await getAttendanceDailySummary({
        token,
        licenseId,
        branchId,
        date: ymd,
      });

      const rows = (Array.isArray(response) ? response : [])
        .map((row) => ({
          employeeId: Number(row?.employeeId || 0),
          fullName: String(row?.fullName || "").trim(),
          dni: row?.dni ? String(row.dni) : "",
          areaId: row?.areaId ?? null,
          areaName: row?.areaName ?? null,
          workedMinutes: Number(row?.workedMinutes || 0),
          latestStatus: row?.latestStatus || null,
          firstClockInAt: row?.firstClockInAt || null,
          lastClockOutAt: row?.lastClockOutAt || null,
        }))
        .filter((row) => Number.isFinite(row.employeeId) && row.employeeId > 0)
        .filter((row) => shouldKeepRow(row));

      setDailyRows(rows);
    } catch {
      // Si falla el refresh, mantenemos la lista actual
    }
  }, [ymd]);

  const handleEmployeeSnapshot = useCallback(() => {
    void refreshDailySummary();
  }, [refreshDailySummary]);

  useEffect(() => {
    void refreshDailySummary();
  }, [refreshDailySummary]);

  useEffect(() => {
    const id = setInterval(() => {
      void refreshDailySummary();
    }, refreshIntervalMs);

    return () => clearInterval(id);
  }, [refreshDailySummary, refreshIntervalMs]);

  const sortedRows = useMemo(
    () =>
      sortRowsStable(
        (Array.isArray(dailyRows) ? dailyRows : []).filter((row) =>
          shouldKeepRow(row)
        )
      ),
    [dailyRows]
  );

  function handleLogout() {
    logout();
    setEmpresaId("");
    setEmpresaNombre("");
    setSucursalId("");
    setSucursalNombre("");
    localStorage.removeItem("sabi_sucursal_nombre");
    navigate("/login", { replace: true });
  }

  return (
    <Box p={2} sx={{ width: "100%", minHeight: "100vh", position: "relative" }}>
      <Box
        sx={{
          width: "100%",
          borderRadius: "18px",
          overflow: "hidden",
          position: "relative",
          border: "1px solid rgba(96,96,113,0.18)",
          boxShadow:
            "inset 0px 0px 4px rgba(117,117,138,0.35), 0px 10px 30px rgba(0,0,0,0.06)",
          background:
            "linear-gradient(135deg, rgba(191,198,225,1) 0%, rgba(175,169,223,1) 55%, rgba(162,218,231,0.9) 100%)",
          px: { xs: 2, sm: 3 },
          py: { xs: 2.25, sm: 3 },
          minHeight: { xs: 120, sm: 140 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(closest-side at 20% 30%, rgba(204,116,192,0.22), transparent 55%)," +
              "radial-gradient(closest-side at 80% 20%, rgba(14,50,149,0.18), transparent 50%)," +
              "radial-gradient(closest-side at 85% 85%, rgba(82,105,173,0.18), transparent 55%)",
            pointerEvents: "none",
          },
        }}
      >
        <Stack spacing={0.5} sx={{ position: "relative", zIndex: 1 }}>
          <Typography sx={{ fontSize: { xs: 22, sm: 26 }, fontWeight: 700, color: "rgba(2,8,23,1)" }}>
            Asistencia{ sucursalNombre ? `, en sucursal: ${sucursalNombre}` : "" }
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: "rgba(2,8,23,0.85)" }}>
            Marcacion de entrada y salida del personal
          </Typography>
        </Stack>

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            height: { xs: 100, sm: 140 },
            width: { xs: 150, sm: 240 },
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            ml: 2,
          }}
        >
          <Box
            component="img"
            src={Ilustracion}
            alt="Ilustración"
            sx={{
              height: "100%",
              width: "100%",
              objectFit: "contain",
              marginTop: "-10px",
              marginBottom: "-10px",
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          mt: 2,
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          alignItems: "flex-start",
          gap: 2,
          width: "100%",
          pb: 10,
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", lg: 420 },
            flexShrink: 0,
          }}
        >
          <AttendanceTerminalPanel
            showEmployeeDetails={false}
            showResetButton={false}
            onEmployeeSnapshotChange={handleEmployeeSnapshot}
          />
        </Box>

        <Card
          sx={{
            width: "100%",
            flex: 1,
            borderRadius: 3,
            border: "1px solid rgba(96,96,113,0.18)",
            minWidth: 0,
          }}
        >
          <CardContent>
            <Stack spacing={1.25}>
              <Typography variant="h6" fontWeight={700}>
                Resumen de jornada de hoy
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Fecha: {ymd}
              </Typography>

              {sortedRows.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Aun no hay empleados con entrada registrada hoy.
                </Typography>
              ) : (
                <AttendanceGroupedByArea
                  rows={sortedRows}
                  renderRow={(row, section) => (
                    <Box
                      sx={{
                        p: 1.2,
                        bgcolor: section?.color?.soft || "#f7f8fb",
                        borderTop: `1px solid ${section?.color?.border || "rgba(0,0,0,0.08)"}`,
                        borderBottom: `1px solid ${section?.color?.border || "rgba(0,0,0,0.08)"}`,
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        justifyContent="space-between"
                      >
                        <Stack spacing={0.4}>
                          <Typography variant="subtitle2" fontWeight={700}>
                            {row.fullName || `Empleado #${row.employeeId}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Entrada: {formatHour(row.firstClockInAt)} | Salida:{" "}
                            {row.latestStatus === "OPEN" ? "-" : formatHour(row.lastClockOutAt)}
                          </Typography>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center">
                          {row.latestStatus !== "OPEN" ? (
                            <Chip
                              label={`Horas: ${formatMinutes(row.workedMinutes)}`}
                              color="primary"
                              variant="outlined"
                              sx={{ fontWeight: 700 }}
                            />
                          ) : null}

                          <Chip
                            label={row.latestStatus === "OPEN" ? "En jornada" : "Fuera de jornada"}
                            color={row.latestStatus === "OPEN" ? "success" : "default"}
                            size="small"
                          />
                        </Stack>
                      </Stack>
                    </Box>
                  )}
                />
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <Button
        variant="contained"
        startIcon={<LogoutRoundedIcon />}
        onClick={handleLogout}
        sx={{
          position: "fixed",
          left: 20,
          bottom: 20,
          zIndex: 1200,
          px: 2,
          py: 1.1,
          borderRadius: "14px",
          textTransform: "none",
          fontWeight: 700,
          bgcolor: "rgba(14, 50, 149, 1)",
          boxShadow: "0px 10px 24px rgba(14, 50, 149, 0.28)",
          "&:hover": {
            bgcolor: "rgba(10, 38, 114, 1)",
          },
        }}
      >
        Cerrar sesión
      </Button>
    </Box>
  );
}